/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { selectAdasProgramsMetaData,
  selectSensingSystemRequirementsReportContent,
  selectSensingSystemRequirementsViewReportDialogOpen,
  selectSensorComponentRequirementsReportContent,
  selectSensorComponentRequirementsViewReportDialogOpen,
  selectWorldModelSolutionRequirementsReportContent,
  selectWorldModelSolutionRequirementsViewReportDialogOpen,
  setSensingSystemRequirementsReportContent,
  setSensingSystemRequirementsViewReportDialogOpen,
  setSensorComponentRequirementsReportContent,
  setSensorComponentRequirementsViewReportDialogOpen,
  setWorldModelSolutionRequirementsReportContent,
  setWorldModelSolutionRequirementsViewReportDialogOpen
} from "../../../../../../redux/ducks/AdasPrograms/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks"
import ArgosDialog, { IArgosDialogProps } from "../../../../../components/Dialogs/ArgosDialog"
import { AdasProgramsReportPageIdx, AdasProgramsReportPages } from "../constants"
import { CAdasProgramsRequests } from "../../types"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import { ReportView } from "../../../../../components/Reports/ReportView"
import { StringParam, useQueryParam } from "use-query-params"
import { getPageFromHtabIndex } from "../helpers"
import { useMemo } from "react"


export const viewReportDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    isDialogOpen: selectSensingSystemRequirementsViewReportDialogOpen,
    setIsDialogOpen: setSensingSystemRequirementsViewReportDialogOpen,
    getReportEndpoint: CAdasProgramsRequests.getReport.url,
    setReportContent: setSensingSystemRequirementsReportContent,
    selectReportContent: selectSensingSystemRequirementsReportContent,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    isDialogOpen: selectSensorComponentRequirementsViewReportDialogOpen,
    setIsDialogOpen: setSensorComponentRequirementsViewReportDialogOpen,
    getReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.getReport,
    setReportContent: setSensorComponentRequirementsReportContent,
    selectReportContent: selectSensorComponentRequirementsReportContent,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    isDialogOpen: selectWorldModelSolutionRequirementsViewReportDialogOpen,
    setIsDialogOpen: setWorldModelSolutionRequirementsViewReportDialogOpen,
    getReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.getReport,
    setReportContent: setWorldModelSolutionRequirementsReportContent,
    selectReportContent: selectWorldModelSolutionRequirementsReportContent,
  },
}

export const ViewReportDialog = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const isDialogOpen = useAppSelector(viewReportDialogSlice[page].isDialogOpen)
  const closeDialog = () => {dispatch(viewReportDialogSlice[page].setIsDialogOpen(false))}

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const reportContent = useAppSelector(viewReportDialogSlice[page].selectReportContent)

  /** App bar title text */
  const fullProgramName = adasProgramMetaData?.fullProgramName
  const reportName = reportContent?.selectedReportData?.reportName
  const headerTitle = `ADAS Program: ${fullProgramName} - ${reportName} Report`

  return (
    <ArgosDialog
      {...VIEW_REPORT_DIALOG_STYLE}
      open={isDialogOpen}
      onClose={closeDialog}
      headerTitle={headerTitle}
    >
      <ReportView />
    </ArgosDialog>
  )
}

const minMaxWidth = '60vw'
const VIEW_REPORT_DIALOG_STYLE: Partial<IArgosDialogProps> = {
  height: '90vh',
  minWidth: minMaxWidth,
  maxWidth: minMaxWidth,
  headerTitleAlign: 'left',
}
